const COPY_BUTTON_SELECTOR = '[data-copy-button]';
const COPY_LABEL_SELECTOR = '[data-copy-label]';
const COPY_ICON_SELECTOR = 'i';

function getCopyText(button: HTMLButtonElement): string | null {
    const explicitValue = button.dataset.copyValue?.trim();
    if (explicitValue) {
        return explicitValue;
    }

    const targetSelector = button.dataset.copyTarget?.trim();
    if (!targetSelector) {
        return null;
    }

    const target = document.querySelector<HTMLElement>(targetSelector);
    if (!target) {
        return null;
    }

    const targetAttribute = button.dataset.copyAttr?.trim();
    if (targetAttribute) {
        const attrValue = target.getAttribute(targetAttribute)?.trim();
        return attrValue || null;
    }

    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
        return target.value.trim() || null;
    }

    return target.textContent?.trim() || null;
}

function setButtonLabel(button: HTMLButtonElement, label: string): void {
    const labelElement = button.querySelector<HTMLElement>(COPY_LABEL_SELECTOR);
    if (labelElement) {
        labelElement.textContent = label;
        return;
    }

    button.setAttribute('aria-label', label);
}

function setButtonIcon(button: HTMLButtonElement, iconClass: string): void {
    const iconElement = button.querySelector<HTMLElement>(COPY_ICON_SELECTOR);
    if (!iconElement) {
        return;
    }

    const tablerIconClass = Array.from(iconElement.classList).find((className) => className.startsWith('ti-'));
    if (tablerIconClass) {
        iconElement.classList.replace(tablerIconClass, iconClass);
        return;
    }

    iconElement.classList.add(iconClass);
}

function fallbackCopyText(text: string): boolean {
    const previouslyFocusedElement = document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', 'readonly');
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';

    try {
        document.body.appendChild(textArea);
        textArea.select();
        textArea.setSelectionRange(0, text.length);
        return document.execCommand('copy');
    } catch {
        return false;
    } finally {
        if (textArea.parentNode) {
            textArea.parentNode.removeChild(textArea);
        }

        if (previouslyFocusedElement && document.contains(previouslyFocusedElement)) {
            previouslyFocusedElement.focus({ preventScroll: true });
        }
    }
}

async function copyText(text: string): Promise<boolean> {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        // Fallback to execCommand below.
    }

    return fallbackCopyText(text);
}

function bindButton(button: HTMLButtonElement): void {
    if (button.dataset.copyBound === 'true') {
        return;
    }

    button.dataset.copyBound = 'true';

    const labelElement = button.querySelector<HTMLElement>(COPY_LABEL_SELECTOR);
    if (labelElement) {
        labelElement.setAttribute('aria-live', 'polite');
        labelElement.setAttribute('aria-atomic', 'true');
        labelElement.setAttribute('role', 'status');
    }

    const iconElement = button.querySelector<HTMLElement>(COPY_ICON_SELECTOR);
    const defaultIconClass = iconElement
        ? Array.from(iconElement.classList).find((className) => className.startsWith('ti-'))
        : null;

    if (defaultIconClass) {
        button.dataset.copyDefaultIcon = defaultIconClass;
    }

    button.addEventListener('click', async () => {
        const defaultLabel = button.dataset.copyDefaultLabel ?? 'Copy';
        const successLabel = button.dataset.copySuccessLabel ?? 'Copied!';
        const errorLabel = button.dataset.copyErrorLabel ?? 'Copy failed';
        const defaultIcon = button.dataset.copyDefaultIcon;
        const successIcon = button.dataset.copySuccessIcon ?? 'ti-check';
        const errorIcon = button.dataset.copyErrorIcon ?? 'ti-alert-circle';

        const copyValue = getCopyText(button);
        if (!copyValue) {
            button.disabled = true;
            setButtonLabel(button, errorLabel);
            setButtonIcon(button, errorIcon);
            window.setTimeout(() => {
                setButtonLabel(button, defaultLabel);
                if (defaultIcon) {
                    setButtonIcon(button, defaultIcon);
                }
                button.disabled = false;
            }, 1600);
            return;
        }

        button.disabled = true;

        let copied = false;
        try {
            copied = await copyText(copyValue);
        } catch {
            copied = false;
        }

        setButtonLabel(button, copied ? successLabel : errorLabel);
        setButtonIcon(button, copied ? successIcon : errorIcon);

        window.setTimeout(() => {
            setButtonLabel(button, defaultLabel);
            if (defaultIcon) {
                setButtonIcon(button, defaultIcon);
            }
            button.disabled = false;
        }, 1600);
    });
}

export function initCopyButtons(): void {
    const buttons = document.querySelectorAll<HTMLButtonElement>(COPY_BUTTON_SELECTOR);
    if (buttons.length === 0) {
        return;
    }

    buttons.forEach(bindButton);
}
